import React from 'react';
import styled from 'styled-components';
import { Message } from '../../types';

const MessageContainer = styled.div<{ isOwn: boolean; isGrouped: boolean }>`
  display: flex;
  align-items: flex-end;
  gap: 0.5rem;
  margin-bottom: ${props => props.isGrouped ? '0.25rem' : '0.75rem'};
  flex-direction: ${props => props.isOwn ? 'row-reverse' : 'row'};
`;

const Avatar = styled.div<{ isAI: boolean; isVisible: boolean }>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${props => props.isAI 
    ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' 
    : 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)'
  };
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 0.8rem;
  flex-shrink: 0;
  visibility: ${props => props.isVisible ? 'visible' : 'hidden'};
  
  ${props => props.isAI && `
    &::after {
      content: '🤖';
      font-size: 16px;
    }
  `}
`;

const MessageContent = styled.div<{ isOwn: boolean; isAI: boolean }>`
  max-width: 70%;
  display: flex;
  flex-direction: column;
  align-items: ${props => props.isOwn ? 'flex-end' : 'flex-start'};
`;

const MessageBubble = styled.div<{ isOwn: boolean; isAI: boolean; isGrouped: boolean }>`
  background: ${props => {
    if (props.isAI) return 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
    if (props.isOwn) return '#667eea';
    return 'white';
  }};
  color: ${props => (props.isOwn || props.isAI) ? 'white' : '#333'};
  padding: 0.75rem 1rem;
  border-radius: ${props => {
    const baseRadius = '18px';
    if (props.isGrouped) {
      if (props.isOwn) return `${baseRadius} 6px 6px ${baseRadius}`;
      return `6px ${baseRadius} ${baseRadius} 6px`;
    }
    if (props.isOwn) return `${baseRadius} ${baseRadius} 6px ${baseRadius}`;
    return `${baseRadius} ${baseRadius} ${baseRadius} 6px`;
  }};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: ${props => (!props.isOwn && !props.isAI) ? '1px solid #e1e5e9' : 'none'};
  position: relative;
  word-wrap: break-word;
  overflow-wrap: break-word;
  line-height: 1.4;
  
  ${props => props.isAI && `
    background-size: 200% 200%;
    animation: gradientShift 3s ease infinite;
    
    @keyframes gradientShift {
      0% { background-position: 0% 50%; }
      50% { background-position: 100% 50%; }
      100% { background-position: 0% 50%; }
    }
  `}
`;

const MessageMeta = styled.div<{ isOwn: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.25rem;
  font-size: 0.75rem;
  color: #666;
  justify-content: ${props => props.isOwn ? 'flex-end' : 'flex-start'};
`;

const Username = styled.span<{ isAI: boolean }>`
  font-weight: 600;
  color: ${props => props.isAI ? '#667eea' : '#333'};
`;

const Timestamp = styled.span`
  color: #999;
`;

const AIBadge = styled.span`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);;
  color: white;
  padding: 0.125rem 0.5rem;
  border-radius: 12px;
  font-size: 0.6rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const MessageText = styled.div`
  white-space: pre-wrap;
  
  /* Style links */
  a {
    color: inherit;
    text-decoration: underline;
    opacity: 0.8;
    
    &:hover {
      opacity: 1;
    }
  }
  
  /* Style code blocks */
  code {
    background: rgba(255, 255, 255, 0.2);
    padding: 0.125rem 0.25rem;
    border-radius: 4px;
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    font-size: 0.85em;
  }
  
  pre {
    background: rgba(255, 255, 255, 0.1);
    padding: 0.5rem;
    border-radius: 8px;
    overflow-x: auto;
    margin: 0.5rem 0;
    
    code {
      background: none;
      padding: 0;
    }
  }
`;

interface MessageItemProps {
  message: Message;
  isOwn: boolean;
  isGrouped: boolean;
  showAvatar: boolean;
}

const MessageItem: React.FC<MessageItemProps> = ({
  message,
  isOwn,
  isGrouped,
  showAvatar
}) => {
  const formatTime = (timestamp: string): string => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getUserInitials = (username: string): string => {
    return username
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const processMessageText = (text: string): string => {
    // Simple URL detection and linking
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    return text.replace(urlRegex, '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>');
  };

  const username = message.user?.username || 'Unknown User';
  const displayText = processMessageText(message.content);

  return (
    <MessageContainer isOwn={isOwn} isGrouped={isGrouped}>
      <Avatar 
        isAI={message.isAiResponse} 
        isVisible={showAvatar}
      >
        {!message.isAiResponse && getUserInitials(username)}
      </Avatar>
      
      <MessageContent isOwn={isOwn} isAI={message.isAiResponse}>
        {!isGrouped && !isOwn && (
          <MessageMeta isOwn={false}>
            <Username isAI={message.isAiResponse}>
              {message.isAiResponse ? 'AI Assistant' : username}
            </Username>
            {message.isAiResponse && <AIBadge>AI</AIBadge>}
          </MessageMeta>
        )}
        
        <MessageBubble 
          isOwn={isOwn} 
          isAI={message.isAiResponse}
          isGrouped={isGrouped}
        >
          <MessageText 
            dangerouslySetInnerHTML={{ __html: displayText }}
          />
        </MessageBubble>
        
        <MessageMeta isOwn={isOwn}>
          <Timestamp>{formatTime(message.timestamp)}</Timestamp>
        </MessageMeta>
      </MessageContent>
    </MessageContainer>
  );
};

export default MessageItem; 